import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "./apiTypes";
import { unwrapData, unwrapList } from "./apiTypes";

export type AdminWalletOverview = {
  balance: number;
  currency: string;
  totals: {
    total_credited: number;
    total_debited: number;
    apple_topups: number;
    course_spend: number;
    admin_adjust_net: number;
    revoked_apple_topups: number;
  };
};

export type AdminWalletLedgerEntry = {
  id: number;
  student_id: number;
  type: string;
  amount: number;
  balance_after: number;
  reference_type?: string | null;
  reference_id?: string | null;
  meta?: Record<string, unknown> | null;
  created_at?: string | null;
};

export type AdminWalletListItem = {
  student_id: number;
  name: string;
  phone: string;
  is_active: boolean;
  balance: number;
  currency: string;
  wallet_updated_at?: string | null;
  created_at?: string | null;
};

export type AdminWalletSummary = {
  currency: string;
  wallets_count: number;
  positive_balance_count: number;
  total_balance: number;
  apple_topups_total: number;
  apple_topups_count: number;
  course_spend_total: number;
  admin_adjust_net: number;
  revoked_apple_topups: number;
};

export type AdminAppleWalletTopup = {
  id: number;
  transaction_id: string;
  original_transaction_id?: string | null;
  product_id: string;
  student_id: number;
  credit_amount: number;
  purchased_at?: string | null;
  revoked_at?: string | null;
  revoke_reason?: string | null;
  environment?: string | null;
  is_revoked: boolean;
  student?: { id: number; name?: string | null; phone?: string | null } | null;
  created_at?: string | null;
};

export type AdminWalletTopupRequest = {
  id: number;
  student_id: number;
  amount: number;
  currency: string;
  payment_method: string;
  payment_proof?: string | null;
  payment_proof_url?: string | null;
  payment_reference?: string | null;
  status: string;
  admin_note?: string | null;
  reviewed_by?: number | null;
  reviewed_at?: string | null;
  created_at?: string | null;
  student?: { id: number; name?: string | null; phone?: string | null } | null;
};

export type AdminWalletAdjustPayload = {
  direction: "credit" | "debit";
  amount: number;
  reason: string;
  payment_method?: string | null;
  reference?: string | null;
};

function unwrapPaginated<T>(payload: ApiEnvelope<PaginatedResult<T>> | undefined): PaginatedResult<T> {
  const result = payload?.result as PaginatedResult<T> | { data?: PaginatedResult<T> } | undefined;
  if (result && "data" in result && result.data && !Array.isArray(result.data)) return result.data;
  return unwrapList<T>(payload);
}

export const adminWalletService = {
  async summary() {
    const response = await api.get<ApiEnvelope<{ data: AdminWalletSummary }>>(
      "/mcp/wallets/summary",
      { meta: { toastSuccess: false } }
    );
    return unwrapData<AdminWalletSummary>(response.data);
  },

  async list(params: {
    page?: number;
    limit?: number;
    term?: string;
    has_balance?: string;
    sort_by?: string;
    sort_direction?: "asc" | "desc";
  } = {}) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminWalletListItem>>>("/mcp/wallets", {
      params,
      meta: { toastSuccess: false },
    });
    return unwrapPaginated(response.data);
  },

  async overview(studentId: number | string) {
    const response = await api.get<ApiEnvelope<{ data: AdminWalletOverview }>>(
      `/mcp/students/${studentId}/wallet`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<AdminWalletOverview>(response.data);
  },

  async ledger(
    studentId: number | string,
    params: { page?: number; limit?: number; type?: string } = {}
  ) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminWalletLedgerEntry>>>(
      `/mcp/students/${studentId}/wallet/ledger`,
      { params, meta: { toastSuccess: false } }
    );
    return unwrapPaginated(response.data);
  },

  async adjust(studentId: number | string, payload: AdminWalletAdjustPayload) {
    const response = await api.post<
      ApiEnvelope<{
        data: {
          wallet: { balance: number; currency: string };
          overview: AdminWalletOverview;
          entry: AdminWalletLedgerEntry;
        };
      }>
    >(`/mcp/students/${studentId}/wallet/adjustments`, payload, { meta: { toast: false } });
    return unwrapData<{
      wallet: { balance: number; currency: string };
      overview: AdminWalletOverview;
      entry: AdminWalletLedgerEntry;
    }>(response.data);
  },

  async studentAppleTopups(
    studentId: number | string,
    params: { page?: number; limit?: number } = {}
  ) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminAppleWalletTopup>>>(
      `/mcp/students/${studentId}/wallet/apple-topups`,
      { params, meta: { toastSuccess: false } }
    );
    return unwrapPaginated(response.data);
  },

  async appleTopups(params: {
    page?: number;
    limit?: number;
    term?: string;
    revoked?: string;
    student_id?: number;
  } = {}) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminAppleWalletTopup>>>(
      "/mcp/wallet-apple-topups",
      { params, meta: { toastSuccess: false } }
    );
    return unwrapPaginated(response.data);
  },

  async topupRequests(params: {
    page?: number;
    limit?: number;
    term?: string;
    status?: string;
    student_id?: number;
  } = {}) {
    const response = await api.get<ApiEnvelope<PaginatedResult<AdminWalletTopupRequest>>>(
      "/mcp/wallet-topup-requests",
      { params, meta: { toastSuccess: false } }
    );
    return unwrapPaginated(response.data);
  },

  async approveTopupRequest(
    id: number | string,
    payload: { amount?: number; admin_note?: string } = {}
  ) {
    const response = await api.post<
      ApiEnvelope<{ data: { id: number; status: string; amount: number; currency: string; student_id: number } }>
    >(`/mcp/wallet-topup-requests/${id}/approve`, payload, { meta: { toast: false } });
    return unwrapData<{ id: number; status: string; amount: number; currency: string; student_id: number }>(
      response.data
    );
  },

  async rejectTopupRequest(id: number | string, payload: { admin_note?: string } = {}) {
    const response = await api.post<ApiEnvelope<{ data: { id: number; status: string } }>>(
      `/mcp/wallet-topup-requests/${id}/reject`,
      payload,
      { meta: { toast: false } }
    );
    return unwrapData<{ id: number; status: string }>(response.data);
  },
};
